import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  newGame,
  applyMove,
  startNextHand,
  optionsForCard,
} from '../game/engine';
import { chooseCpuMove } from '../ai/aiEngine';
import { cardLabel, cardsLabel } from '../game/cards';
import audioManager from '../audio/audioManager';
import { loadGame, saveGame, clearGame, recordResult } from '../store/persistence';

const CPU_DELAY = 1050;

export default function useScopaGame({ mode, settings }) {
  const [state, setState] = useState(() => {
    if (mode === 'continue') return loadGame() || newGame();
    clearGame();
    return newGame();
  });
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [captureOptions, setCaptureOptions] = useState(null); // array of card-arrays or null
  const [live, setLive] = useState('');
  const [captureFx, setCaptureFx] = useState(null); // signals a capture for the flying-card animation
  const fxNonce = useRef(0);
  const liveQueue = useRef([]);
  const liveTimer = useRef(null);
  const liveNonce = useRef(0);

  // Announce messages sequentially so each one is actually committed to the
  // aria-live region (screen readers only pick up real DOM changes). A
  // zero-width toggle guarantees a DOM change even for repeated messages.
  const flushLive = useCallback(() => {
    if (liveTimer.current) return;
    const step = () => {
      const item = liveQueue.current.shift();
      if (item === undefined) {
        liveTimer.current = null;
        return;
      }
      liveNonce.current += 1;
      setLive(item.text + (liveNonce.current % 2 ? '\u200B' : ''));
      liveTimer.current = setTimeout(step, 1600);
    };
    step();
  }, []);

  // Intelligent aria-live queue. Messages are either events (default: always
  // spoken, in order) or transient state cues carrying a dedupe `key` ('table'
  // for the table remainder, 'turn' for the turn cue). Pushing a keyed message
  // supersedes any NOT-YET-SPOKEN message with the same key, so VoiceOver never
  // reads a stale table/turn under fast play. A safety cap drops the oldest
  // STATE messages first (never events). Timing (1600ms) is unchanged.
  const pushLive = useCallback(
    (text, key) => {
      if (!text) return;
      if (key) {
        liveQueue.current = liveQueue.current.filter((m) => m.key !== key);
      }
      liveQueue.current.push({ text, key: key || null });
      const MAX = 6;
      while (liveQueue.current.length > MAX) {
        const idx = liveQueue.current.findIndex((m) => m.key);
        if (idx === -1) break;
        liveQueue.current.splice(idx, 1);
      }
      flushLive();
    },
    [flushLive]
  );

  useEffect(
    () => () => {
      clearTimeout(liveTimer.current);
      liveTimer.current = null;
    },
    []
  );

  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const stateRef = useRef(state);
  stateRef.current = state;
  const cpuTimer = useRef(null);

  // Persist the freshly dealt game right away so it already counts as "in
  // progress" (enables Continua partita and the difficulty-change confirmation
  // from the very first hand). saveGame no-ops once the game is over.
  useEffect(() => {
    saveGame(stateRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const telecronaca = useCallback(
    (text, key) => {
      const s = settingsRef.current;
      if (!s.accessibleMode || !s.narrationEnabled) return;
      // Single channel: the aria-live region only (read by VoiceOver). We do NOT
      // also speak via the app's SpeechSynthesis voice — that parallel channel
      // caused overlapping / desynced double readings for VoiceOver users.
      pushLive(text, key);
    },
    [pushLive]
  );

  // Initial telecronaca at the very start of a match: "Inizia la partita" + the
  // table read in the SAME order as "Leggi tavolo" + whose turn it is. Emitted
  // ONCE through the normal telecronaca queue, so it never overlaps "Leggi
  // tavolo" or other announcements. Never reveals the player's own cards.
  const emitStartAnnouncement = useCallback(
    (s) => {
      const freshStart =
        s.phase === 'playing' &&
        s.handNumber === 1 &&
        s.table.length === 4 &&
        s.hands.player.length === 3 &&
        s.hands.cpu.length === 3 &&
        s.captured.player.length === 0 &&
        s.captured.cpu.length === 0;
      if (!freshStart) return;
      const tableText =
        s.table.length === 0
          ? 'Il tavolo è vuoto'
          : `Sul tavolo ci sono ${cardsLabel(s.table)}`;
      const turnText =
        s.turn === 'player' ? 'È il tuo turno.' : "È il turno dell'avversario.";
      telecronaca(`Inizia la partita. ${tableText}. ${turnText}`);
    },
    [telecronaca]
  );

  const startAnnouncedRef = useRef(false);
  useEffect(() => {
    if (startAnnouncedRef.current) return;
    startAnnouncedRef.current = true;
    emitStartAnnouncement(stateRef.current);
  }, [emitStartAnnouncement]);

  const status = useCallback(
    (text, key) => {
      pushLive(text, key);
    },
    [pushLive]
  );

  const processEvents = useCallback(
    (events) => {
      const s = settingsRef.current;
      for (const ev of events) {
        if (ev.type === 'play') {
          audioManager.playCard();
          if (ev.actor === 'cpu') {
            telecronaca(`L'avversario ha giocato ${cardLabel(ev.played)}.`);
          }
        } else if (ev.type === 'capture') {
          fxNonce.current += 1;
          setCaptureFx({
            nonce: fxNonce.current,
            actor: ev.actor,
            played: ev.played,
            captured: ev.captured,
          });
          if (ev.scopa) {
            audioManager.scopa();
          } else {
            audioManager.capture();
          }
          if (ev.actor === 'cpu') {
            // Narrate the opponent's capture: card played + card(s) taken + with
            // which card. The following CPU-turn effect adds the remaining table
            // and whose turn it is (and correctly omits the turn when the hand
            // ends), so we don't repeat them here. Player cards are never read.
            let msg;
            if (ev.scopa) {
              msg = `L'avversario ha giocato ${cardLabel(
                ev.played
              )} e ha preso tutte le carte sul tavolo. Scopa!`;
            } else {
              msg = `L'avversario ha giocato ${cardLabel(
                ev.played
              )} e ha preso ${cardsLabel(ev.captured)} con ${cardLabel(
                ev.played
              )}.`;
            }
            telecronaca(msg);
          } else {
            // Player's own capture is NOT narrated (no redundant telecronaca),
            // but Scopa is an important event.
            if (ev.scopa) {
              telecronaca('Scopa!');
              toast.success('Scopa!', { description: 'Hai spazzato il tavolo!' });
            }
          }
          if (ev.scopa && ev.actor === 'cpu') {
            toast('Scopa dell\u2019avversario', {
              description: 'L\u2019avversario ha spazzato il tavolo.',
            });
          }
        } else if (ev.type === 'refill') {
          telecronaca('Nuove carte distribuite.');
        } else if (ev.type === 'handOver') {
          const p = ev.matchScores.player;
          const c = ev.matchScores.cpu;
          toast('Fine della mano', {
            description: `Punteggio partita — Tu ${p}, Avversario ${c}.`,
          });
          telecronaca(
            `Fine della mano. Punteggio della partita: tu ${p}, avversario ${c}.`
          );
        } else if (ev.type === 'gameOver') {
          recordResult(ev.winner);
          clearGame();
          if (ev.winner === 'player') {
            audioManager.win();
            telecronaca('Hai vinto la partita!');
          } else {
            telecronaca('L\u2019avversario ha vinto la partita.');
          }
        }
      }
    },
    [telecronaca]
  );

  const applyAndProcess = useCallback(
    (actor, cardId, captureIds) => {
      const { state: next, events } = applyMove(
        stateRef.current,
        actor,
        cardId,
        captureIds
      );
      processEvents(events);
      stateRef.current = next;
      setState(next);
      saveGame(next);
      return { next, events };
    },
    [processEvents]
  );

  // Drive the CPU turn with a natural delay.
  useEffect(() => {
    if (state.phase !== 'playing' || state.turn !== 'cpu') return;
    cpuTimer.current = setTimeout(() => {
      const move = chooseCpuMove(stateRef.current, settingsRef.current.difficulty);
      const { next } = applyAndProcess('cpu', move.cardId, move.captureIds);
      // After the opponent's turn, report the table (accessible mode only).
      if (settingsRef.current.accessibleMode && next.phase === 'playing') {
        const t = next.table;
        const text =
          t.length === 0
            ? 'Il tavolo è vuoto.'
            : `Sul tavolo rimangono ${cardsLabel(t)}.`;
        telecronaca(text, 'table');
      }
      if (next.phase === 'playing' && next.turn === 'player') {
        status('È il tuo turno.', 'turn');
      }
    }, CPU_DELAY);
    return () => clearTimeout(cpuTimer.current);
  }, [state.turn, state.phase, applyAndProcess, telecronaca, status]);

  const clearSelection = useCallback(() => {
    setSelectedCardId(null);
    setCaptureOptions(null);
  }, []);

  const selectCard = useCallback(
    (cardId) => {
      const cur = stateRef.current;
      if (cur.phase !== 'playing' || cur.turn !== 'player') {
        audioManager.error();
        status('Non è il tuo turno.');
        return;
      }
      // Toggle off if re-selecting the pending card.
      if (selectedCardId === cardId && captureOptions) {
        audioManager.deselect();
        clearSelection();
        const card = cur.hands.player.find((c) => c.id === cardId);
        if (card) status(`${cardLabel(card)}, deselezionata.`);
        return;
      }
      const options = optionsForCard(cur, 'player', cardId);
      const card = cur.hands.player.find((c) => c.id === cardId);
      if (options.length === 0) {
        // No valid capture -> simply play the card.
        audioManager.playCard();
        applyAndProcess('player', cardId, []);
        clearSelection();
      } else if (options.length === 1) {
        // Forced / single capture -> execute immediately.
        applyAndProcess('player', cardId, options[0].map((c) => c.id));
        clearSelection();
      } else {
        // Multiple combinations -> let the player choose.
        audioManager.select();
        setSelectedCardId(cardId);
        setCaptureOptions(options);
        const list = options.map((o) => cardsLabel(o)).join('. ');
        status(
          `${card ? cardLabel(card) : ''}, selezionata. Prese disponibili: ${list}.`
        );
      }
    },
    [selectedCardId, captureOptions, applyAndProcess, clearSelection, status]
  );

  const chooseCapture = useCallback(
    (index) => {
      if (!captureOptions || !selectedCardId) return;
      const option = captureOptions[index];
      if (!option) {
        audioManager.error();
        status('Selezione non valida.');
        return;
      }
      applyAndProcess(
        'player',
        selectedCardId,
        option.map((c) => c.id)
      );
      clearSelection();
    },
    [captureOptions, selectedCardId, applyAndProcess, clearSelection, status]
  );

  const readTable = useCallback(() => {
    const t = stateRef.current.table;
    const text =
      t.length === 0
        ? 'Il tavolo è vuoto.'
        : `Sul tavolo ci sono ${cardsLabel(t)}.`;
    // Cancel any queued telecronaca so the table reading is not interleaved.
    liveQueue.current = [];
    clearTimeout(liveTimer.current);
    liveTimer.current = null;
    // Single announcement channel: the aria-live region (read by VoiceOver).
    // A zero-width toggle forces VoiceOver to re-read on every press, even when
    // the table text is identical to the previous read. We intentionally do NOT
    // also speak via the app SpeechSynthesis here: on the first press its voice
    // overlapped VoiceOver (two simultaneous readings). One channel = one voice.
    liveNonce.current += 1;
    setLive(text + (liveNonce.current % 2 ? '\u200B' : ''));
  }, []);

  const continueNextHand = useCallback(() => {
    // Idempotency guard: a repeated / rapid tap must not deal (and discard)
    // extra hands or alternate the dealer more than once. Once startNextHand
    // has run, phase is 'playing' again, so any extra call is ignored here.
    if (stateRef.current.phase !== 'handOver') return;
    const next = startNextHand(stateRef.current);
    stateRef.current = next;
    setState(next);
    saveGame(next);
    clearSelection();
    status(next.turn === 'player' ? 'Nuova mano. È il tuo turno.' : "Nuova mano. È il turno dell'avversario.");
  }, [clearSelection, status]);

  const restart = useCallback(() => {
    clearGame();
    const g = newGame();
    stateRef.current = g;
    setState(g);
    // Persist immediately so a reload after "Gioca ancora" resumes THIS game
    // (not a fresh random deal) and Home/Impostazioni see a game in progress.
    saveGame(g);
    clearSelection();
    // Reset the live-region queue so the previous match's last message
    // (e.g. "Fine della mano ...") is not left announced.
    liveQueue.current = [];
    clearTimeout(liveTimer.current);
    liveTimer.current = null;
    setLive('');
    // Re-emit the opening announcement for the new match (accessible mode).
    startAnnouncedRef.current = true;
    emitStartAnnouncement(g);
  }, [clearSelection, emitStartAnnouncement]);

  return {
    state,
    selectedCardId,
    captureOptions,
    live,
    captureFx,
    selectCard,
    chooseCapture,
    cancelSelection: clearSelection,
    readTable,
    continueNextHand,
    restart,
  };
}
